package dev.lingvo.shared

/** Validate the complete captured envelope before a caller evaluates or sends any partial scope. */
fun validateReleaseResourcesRequest(request: ReleaseResourcesRequest): String? {
    validateLocale(request.sourceLocale)?.let { return "Invalid source locale: $it" }
    if (request.locales.isEmpty() || request.locales.size > MAX_RELEASE_LOCALES) return "Select 1..$MAX_RELEASE_LOCALES target locales."
    if (request.locales.map(String::lowercase).distinct().size != request.locales.size) return "Target locales must be distinct."
    for (locale in request.locales) {
        validateLocale(locale)?.let { return "Invalid target locale: $it" }
        if (locale.equals(request.sourceLocale, ignoreCase = true)) return "Source locale is not a target review scope."
    }
    if (request.files.isEmpty() || request.files.size > MAX_RELEASE_LOGICAL_FILES) return "Select 1..$MAX_RELEASE_LOGICAL_FILES logical files."
    if (request.resources.isEmpty() || request.resources.size > MAX_RELEASE_PHYSICAL_RESOURCES) return "Resource pool must contain 1..$MAX_RELEASE_PHYSICAL_RESOURCES paths."
    if (request.files.map { normalizedReleasePath(it.path) }.distinct().size != request.files.size) return "Logical source paths must be distinct."
    if (request.resources.map { normalizedReleasePath(it.path) }.distinct().size != request.resources.size) return "Physical resource paths and aliases must be distinct."
    val pool = request.resources.associateBy(ReleaseResourceDto::path)
    var bytes = 0L
    for (resource in request.resources) {
        validateReleasePath(resource.path)?.let { return it }
        val content = resource.content ?: continue
        if (!hasValidReleaseUnicode(content)) return "Resource content must be valid UTF-8 text."
        if (content.length > MAX_RELEASE_RESOURCE_BYTES) return "Resource exceeds the per-file byte limit."
        val size = content.encodeToByteArray().size
        if (size > MAX_RELEASE_RESOURCE_BYTES) return "Resource exceeds the per-file byte limit."
        bytes += size
        if (bytes > MAX_RELEASE_TOTAL_BYTES) return "Captured content exceeds the complete request byte limit."
    }
    val owners = mutableMapOf<String, String>()
    val used = mutableSetOf<String>()
    val ids = mutableSetOf<String>()
    for (file in request.files) {
        validateReleasePath(file.path)?.let { return it }
        if (file.fileId != null && (!isCanonicalSourceReviewId(file.fileId) || !ids.add(file.fileId))) return "File IDs must be distinct canonical UUIDs."
        if (file.source.isEmpty() || file.source.first() != file.path) return "The logical source path must be its first physical source."
        val sourceProblem = validateReleaseParts(file.source, pool)
        if (sourceProblem != null) return sourceProblem
        if (file.translations.map(ReleaseLocaleResourcesDto::locale).toSet() != request.locales.toSet() || file.translations.size != request.locales.size) {
            return "Every logical file must contain exactly the requested target locale set."
        }
        val sourceFormats = file.source.map { pool.getValue(it).format }
        for (translation in file.translations) {
            validateReleaseParts(translation.resources, pool)?.let { return it }
            if (translation.resources.map { pool.getValue(it).format } != sourceFormats) return "Source and target physical format scopes must match."
        }
        val allPaths = file.source + file.translations.flatMap(ReleaseLocaleResourcesDto::resources)
        for (path in allPaths) {
            val previous = owners.put(path, file.path)
            if (previous != null && previous != file.path) return "Logical files cannot share a physical output."
            used += path
        }
        if (allPaths.size != allPaths.distinct().size) {
            val sharedCatalogue = sourceFormats == listOf(FileFormat.XCSTRINGS) &&
                file.translations.all { it.resources == file.source }
            if (!sharedCatalogue) return "Physical paths collide across source/target locale roles."
        }
    }
    if (used != pool.keys) return "Resource pool contains unused or unrepresented physical files."
    return null
}

private fun validateReleaseParts(paths: List<String>, pool: Map<String, ReleaseResourceDto>): String? {
    if (paths.isEmpty() || paths.size > 2 || paths.distinct().size != paths.size) return "A physical scope must contain one resource or its iOS companion pair."
    if (paths.any { it !in pool }) return "A physical path is missing from the captured resource pool."
    if (paths.size == 2) {
        if (pool.getValue(paths[0]).format != FileFormat.IOS_STRINGS || pool.getValue(paths[1]).format != FileFormat.IOS_STRINGSDICT) {
            return "Only a strings/stringsdict companion pair may share a logical file."
        }
        if (releaseCompanionPath(paths[0]) != paths[1]) return "The stringsdict path is not the declared strings companion."
    }
    return null
}

fun releaseCompanionPath(path: String): String =
    if (path.endsWith(".strings", ignoreCase = true)) path.dropLast(".strings".length) + ".stringsdict" else path + ".stringsdict"

fun normalizedReleasePath(path: String): String = path.replace('\\', '/').split('/').filter { it.isNotEmpty() && it != "." }.joinToString("/")

fun validateReleasePath(path: String): String? = when {
    path.isBlank() || path.length > 1024 || !hasValidReleaseUnicode(path) -> "Resource path must be bounded nonblank text."
    '\u0000' in path || path.startsWith('/') || path.startsWith('\\') || Regex("^[A-Za-z]:").containsMatchIn(path) -> "Resource path must be relative to the checkout."
    path.replace('\\', '/').split('/').any { it == ".." } || normalizedReleasePath(path).isEmpty() -> "Resource path escapes or does not name a checkout file."
    else -> null
}

private fun hasValidReleaseUnicode(value: String): Boolean {
    var index = 0
    while (index < value.length) {
        val character = value[index]
        if (character in '\uD800'..'\uDBFF') {
            if (index + 1 >= value.length || value[index + 1] !in '\uDC00'..'\uDFFF') return false
            index++
        } else if (character in '\uDC00'..'\uDFFF') return false
        index++
    }
    return true
}

/** Required verdict/count/sample relationships prevent a malformed response from becoming green. */
fun validateReleaseCheckReport(report: ReleaseCheckReportDto): String? {
    if (report.passed && !report.complete) return "An incomplete check cannot pass."
    if (report.policy.isBlank() || report.evaluatedAt.isBlank()) return "Policy and evaluation time are required."
    if (report.passed && (report.scope.filePaths.isEmpty() || report.scope.locales.isEmpty())) return "A passing report requires a nonempty file and target scope."
    validateLocale(report.scope.sourceLocale)?.let { return "Invalid report source locale." }
    for (locale in report.scope.locales) {
        validateLocale(locale)?.let { return "Invalid report target locale." }
        if (locale.equals(report.scope.sourceLocale, ignoreCase = true)) return "Source locale is not a target report scope."
    }
    if (report.scope.locales.map(String::lowercase).distinct().size != report.scope.locales.size) return "Report locales are duplicated."
    if (report.scope.filePaths.map(::normalizedReleasePath).distinct().size != report.scope.filePaths.size) return "Report file scope is duplicated."
    if (report.scope.filePaths.any { validateReleasePath(it) != null }) return "Report file scope is invalid."
    if (report.scope.kind !in setOf(RELEASE_SCOPE_LOCAL_RESOURCES, RELEASE_SCOPE_HOSTED_PROJECT, RELEASE_SCOPE_CHECKOUT_RESOURCES)) return "Unknown evaluation scope."
    if (report.scope.kind != RELEASE_SCOPE_LOCAL_RESOURCES && report.scope.projectSlug.isNullOrBlank()) return "Hosted report must identify its project."
    val counts = report.counts
    if (listOf(counts.logicalFiles, counts.physicalResources, counts.sourceKeys, counts.targetStrings, counts.excludedKeys, counts.findings, counts.errors, counts.warnings).any { it < 0 }) return "Report counts cannot be negative."
    if (counts.logicalFiles != report.scope.filePaths.size || counts.excludedKeys > counts.sourceKeys) return "Report scope and counts disagree."
    if (counts.errors.toLong() + counts.warnings != counts.findings.toLong()) return "Every finding must have a counted severity."
    if (counts.byCode.any { it.key.isBlank() || it.value < 0 } || counts.byCode.values.sumOf(Int::toLong) != counts.findings.toLong()) return "Authoritative finding codes and total disagree."
    if (report.findings.size > counts.findings || report.findings.size > MAX_RELEASE_FINDINGS || report.truncated != (report.findings.size < counts.findings)) return "Diagnostic sample and authoritative count disagree."
    if (report.passed && counts.errors > 0) return "A report containing blocking errors cannot pass."
    if (report.passed && counts.sourceKeys == 0) return "An empty source scope cannot pass."
    for (finding in report.findings) {
        if (finding.code.isBlank() || finding.message.isBlank()) return "Findings require a code and recovery message."
        if (finding.keyId != null && !isCanonicalSourceReviewId(finding.keyId)) return "Invalid finding key identity."
        if (finding.targetRevision != null && !isCanonicalSourceReviewId(finding.targetRevision)) return "Invalid finding target revision."
        if (finding.currentSourceView != null && (validateSourceView(finding.currentSourceView) != null ||
                finding.keyId != null && finding.currentSourceView.keyId != finding.keyId)) return "Invalid finding source view."
    }
    for (digest in report.digests) {
        if (digest.sha256 == null && digest.byteCount != null || digest.sha256 != null && digest.byteCount == null) return "Digest and byte count presence disagree."
        if (digest.sha256 != null && (!Regex("^[0-9a-f]{64}$").matches(digest.sha256) || digest.byteCount!! < 0)) return "Invalid captured byte digest."
    }
    return null
}
